/* eslint-disable array-bracket-newline */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import BigNumber from 'bignumber.js';
import { ITicker } from '@kattana-io/common';
import Text from '../../../../atoms/Text';
import Image from '../../../../atoms/Image';
import TradingBox from '../../../../molecules/TradingBox';
import ParamField from '../../common/ParamField';
import NonEditableParamField from '../../common/NonEditableParamField';
import GraphTooltip from '../../common/GraphTooltip';
import ControlItem from '../../common/ControlItem';
import GasPanel from '../../common/GasPanel';
import CreateButton from '../../common/CreateButton';
import stop_price_graph from '../../images/stop_price.png';
import limit_price_graph from '../../images/limit_price.png';
import add_grid_graph from '../../images/add_grid.png';
import wallet from '../../../../assets/images/wallet_grey.svg';
import settings from '../../../../assets/images/settings.svg';
import big_arrow from '../../../../assets/images/arrow_down.svg';
import NotAvailable from './NotAvailable';
import NotSupported from './NotSupported';
import Deploying from './Deploying';
import {
  LimitActions,
  LimitModes,
  LimitTabs,
} from '../../reducers/limitReducer';
import { getPercentFromBalance } from '../../helpers/balanceFormatters';
import { getLimitExecutionPrice, getLimitFee } from '../../helpers/statistics';
import { actionShowModal } from '../../../../actions/modal';
import { EOverlays, ETabs, ICoin } from '../../reducers/tradingReducer';
import { ModalTypes } from '../../../../reducers/modal';
import { NativeCoin } from '../../helpers/nativeCoins';
import useLimit from './useLimit';
import { validateSettingsValue } from '../../helpers/settingsFormatters';
import { OnRefresh } from '../../../../wallet/walletContext';

export interface ILimitProps {
  tab: ETabs;
  setTab: (val: ETabs) => void;
  setOverlay: (overlay: EOverlays) => void;
  dexChainId?: number;
  chainId: number | undefined;
  base: ICoin;
  quote: ICoin;
  meta?: ITicker;
  account: string | null | undefined;
  balancesAddress: any;
  ratesAddress: any;
  current: ITicker;
  onFlip: () => void;
  onSetBaseAmount: (input: BigNumber) => void;
  onSetQuoteAmount: (input: BigNumber) => void;
  onSetTradesCount: (input: number) => void;
  onNotify: (input: any) => void;
  onWalletRefresh: OnRefresh;
  slippage: number;
  tradesCount: number;
  sendTokens: boolean;
}

const Limit = (props: ILimitProps) => {
  const appDispatch = useDispatch();
  const {
    base,
    quote,
    account,
    meta,
    setTab,
    onFlip,
    onNotify,
    onSetTradesCount,
  } = props;

  const [scroll, setScroll] = useState(false);
  const widget = useRef<HTMLDivElement>(null);

  const {
    limitTab,
    network,
    isSameChain,
    baseBalance,
    quoteBalance,
    limitMode,
    limitPrice,
    stopPrice,
    slippage,
    tradesCount,
    isGrid,
    grid,
    onBaseUpdate,
    onQuoteUpdate,
    onDeployRequested,
    onCreateOrder,
    dispatch,
  } = useLimit(props);

  useEffect(() => {
    const parent = widget.current?.parentNode as HTMLDivElement;

    if (parent) {
      setScroll(parent.clientHeight < 445);
    }
  }, [widget]);

  useEffect(() => {
    const payload = isGrid
      ? (grid.initPrice + grid.finalPrice) / 2
      : limitPrice;
    dispatch({ type: LimitActions.SetStopPrice, payload });
  }, [dispatch, grid.finalPrice, grid.initPrice, isGrid, limitPrice]);

  const nativeCoin = NativeCoin(network?.toDecimal() || 1, new BigNumber(0));

  const createCondition = base.amount.isEqualTo(0) || !meta;

  const tradesNumber = isGrid ? grid.ordersNum : tradesCount;

  const renderContent = () => {
    switch (limitTab) {
    case LimitTabs.NotSupported:
      return (
        <NotSupported
          scroll={scroll}
          account={!!account}
          toMarket={() => setTab(ETabs.Swap)}
        />
      );
    case LimitTabs.NotAvailable:
      return (
        <NotAvailable
          scroll={scroll}
          onDeployRequested={onDeployRequested}
          toMarket={() => setTab(ETabs.Swap)}
        />
      );
    case LimitTabs.Deploying:
      return <Deploying scroll={scroll} />;
    default:
      return (
        <>
          <TradingBox
            network={network as any}
            disabled={!isSameChain}
            title='Send'
            balance={baseBalance}
            coinAddress={base.address}
            coinTitle={base.name}
            value={base.amount}
            onChange={onBaseUpdate}
            setTab={() => props.setOverlay(EOverlays.BaseCurrencyMenu)}
            isCoinChangable
          />
          <div className='row percents'>
            <Text
              size={12}
              className={`percents__item ${
                !base.amount.eq(0) &&
                base.amount.eq(getPercentFromBalance(baseBalance, 0.2))
                  ? 'percents__item--active'
                  : ''
              }`}
              onClick={() =>
                onBaseUpdate(getPercentFromBalance(baseBalance, 0.2))
              }
            >
              20%
            </Text>
            <Text
              size={12}
              className={`percents__item ${
                !base.amount.eq(0) &&
                base.amount.eq(getPercentFromBalance(baseBalance, 0.35))
                  ? 'percents__item--active'
                  : ''
              }`}
              onClick={() =>
                onBaseUpdate(getPercentFromBalance(baseBalance, 0.35))
              }
            >
              35%
            </Text>
            <div className='big_arrow'>
              <Image src={big_arrow} size='s' onClick={onFlip} />
            </div>
            <Text
              size={12}
              className={`percents__item ${
                !base.amount.eq(0) &&
                base.amount.eq(getPercentFromBalance(baseBalance, 0.5))
                  ? 'percents__item--active'
                  : ''
              }`}
              onClick={() =>
                onBaseUpdate(getPercentFromBalance(baseBalance, 0.5))
              }
            >
              50%
            </Text>
            <Text
              size={12}
              className={`percents__item ${
                !base.amount.eq(0) &&
                base.amount.eq(getPercentFromBalance(baseBalance, 0.65))
                  ? 'percents__item--active'
                  : ''
              }`}
              onClick={() =>
                onBaseUpdate(getPercentFromBalance(baseBalance, 0.65))
              }
            >
              65%
            </Text>
          </div>
          <TradingBox
            network={network as any}
            disabled={!isSameChain}
            title='Receive'
            balance={quoteBalance}
            coinAddress={quote.address}
            coinTitle={quote.name}
            value={quote.amount}
            onChange={onQuoteUpdate}
            setTab={() => props.setOverlay(EOverlays.QuoteCurrencyMenu)}
            isCoinChangable
          />
          <div className='tab_selector'>
            <div className='tab' onClick={() => setTab(ETabs.Swap)}>
              <Text size={12}>Market</Text>
            </div>
            <div
              className='tab tab--active'
              onClick={() => setTab(ETabs.Limit)}
            >
              <Text size={14}>Limit</Text>
            </div>
          </div>
          <div className='limit_modes'>
            <div
              data-for='limit-modes'
              data-tip='Execute order when<br />amount out is guaranteed,<br />executed at price + slippage'
              data-event-off='mouseleave click'
              onClick={() =>
                dispatch({
                  payload: LimitModes.Overline,
                  type: LimitActions.SetLimitMode,
                })
              }
              className={`limit_modes__mode ${
                limitMode === LimitModes.Overline
                  ? 'limit_modes__mode--active'
                  : 'limit_modes__mode--normal'
              }`}
            >
              <Text size={12}>Overline</Text>
            </div>
            <div
              data-for='limit-modes'
              data-tip='Execute order when<br />price reach this level'
              data-event-off='mouseleave click'
              onClick={() =>
                dispatch({
                  payload: LimitModes.StopLimit,
                  type: LimitActions.SetLimitMode,
                })
              }
              className={`limit_modes__mode ${
                limitMode === LimitModes.StopLimit
                  ? 'limit_modes__mode--active'
                  : 'limit_modes__mode--normal'
              }`}
            >
              <Text size={12}>Stop limit</Text>
            </div>
          </div>
          <ReactTooltip
            id='limit-modes'
            type='dark'
            place='bottom'
            className='text_tooltip'
            multiline={true}
          />
          <div className='params_panel'>
            <NonEditableParamField
              title='Stop price'
              text={`${stopPrice.toFixed(4)} ${quote.name}`}
              getTooltip={() => (
                <GraphTooltip
                  title='Stop price'
                  description="Price from which we're starting our trading session"
                  imageUrl={stop_price_graph}
                  height={157}
                />
              )}
            />
            <NonEditableParamField
              title={!isGrid ? 'Remove Limit price' : 'Add Limit price'}
              expanded={!isGrid}
              onExpand={() => dispatch({ type: LimitActions.ToggleGrid })}
              getTooltip={() => (
                <GraphTooltip
                  title='Limit price'
                  description="Price to which we're doing our trading session"
                  imageUrl={limit_price_graph}
                  height={156}
                />
              )}
            />
            {!isGrid && (
              <div className='params_panel__expanded'>
                <ParamField
                  title='Limit price'
                  value={limitPrice.toFixed(4)}
                  onSave={(value) =>
                    dispatch({
                      type: LimitActions.SetLimitPrice,
                      payload: value,
                    })
                  }
                  ending={quote.name}
                />
                <ParamField
                  title='Trades count'
                  value={tradesCount.toFixed(0)}
                  onSave={onSetTradesCount}
                  validateValue={(value) =>
                    validateSettingsValue(value, 1, 99, true)
                  }
                />
              </div>
            )}
            <NonEditableParamField
              title={isGrid ? 'Remove Grid' : 'Add Grid'}
              expanded={isGrid}
              onExpand={() => dispatch({ type: LimitActions.ToggleGrid })}
              getTooltip={() => (
                <GraphTooltip
                  title='Add grid'
                  description='Plan multiple trades with different prices within one order'
                  imageUrl={add_grid_graph}
                  height={184}
                />
              )}
            />
            {isGrid && (
              <div className='params_panel__expanded'>
                <ParamField
                  title='Initial price'
                  value={grid.initPrice.toFixed(4)}
                  onSave={(value) =>
                    dispatch({
                      type: LimitActions.SetGridInitPrice,
                      payload: value,
                    })
                  }
                  ending={quote.name}
                />
                <ParamField
                  title='Final price'
                  value={grid.finalPrice.toFixed(4)}
                  onSave={(value) =>
                    dispatch({
                      type: LimitActions.SetGridFinalPrice,
                      payload: value,
                    })
                  }
                  ending={quote.name}
                />
                <ParamField
                  title='Number of orders'
                  value={grid.ordersNum.toFixed(0)}
                  onSave={(value) =>
                    dispatch({
                      type: LimitActions.SetGridOrdersNum,
                      payload: value,
                    })
                  }
                  validateValue={(value) =>
                    validateSettingsValue(value, 2, 99, true)
                  }
                />
              </div>
            )}
          </div>
          {isSameChain && (
            <GasPanel
              account={props.account}
              chainId={network?.toDecimal()}
              onNotify={onNotify}
            />
          )}
          <div className='stats_panel'>
            <div className='btn_margin'>
              <CreateButton
                createCondition={createCondition}
                onCreate={onCreateOrder}
                label={meta ? 'Create' : 'No pair with enough liquidity'}
                chainId={network?.toDecimal()}
                isCorrectChain={isSameChain}
              />
            </div>
            <div className='stats_panel__stat'>
              <div className='params_panel__param__property'>
                <Text size={12} className='stats_panel__stat__title'>
                  Execution price
                </Text>
                <div
                  data-tip='the price at which the<br />Limit Order will actually made'
                  data-for='limit-stats'
                  className='params_panel__param__property__icon icon-tooltip-circle'
                />
              </div>
              <Text size={12} className='stats_panel__stat__value'>
                {getLimitExecutionPrice(stopPrice, slippage).toFixed(4)}
              </Text>
            </div>
            <div className='stats_panel__stat'>
              <div className='params_panel__param__property'>
                <Text size={12} className='stats_panel__stat__title'>
                  Return of tokens
                </Text>
                <div
                  data-tip='maximum deviation<br />from received tokens'
                  data-for='limit-stats'
                  className='params_panel__param__property__icon icon-tooltip-circle'
                />
              </div>
              <Text size={12} className='stats_panel__stat__value'>
                {slippage}%
              </Text>
            </div>
            <div className='stats_panel__stat'>
              <div className='params_panel__param__property'>
                <Text size={12} className='stats_panel__stat__title'>
                  Total Fee
                </Text>
                <div
                  data-tip='maximum fees to<br />pay for the limit order'
                  data-for='limit-stats'
                  className='params_panel__param__property__icon icon-tooltip-circle'
                />
              </div>
              <Text size={12} className='stats_panel__stat__value'>
                {getLimitFee(tradesNumber)} {nativeCoin.name}
              </Text>
            </div>
            <div className='stats_panel__stat'>
              <div className='params_panel__param__property'>
                <Text size={12} className='stats_panel__stat__title'>
                  Number of transaction
                </Text>
                <div
                  data-tip='maximum number of<br />transactions to be made for<br />limit order execution'
                  data-for='limit-stats'
                  className='params_panel__param__property__icon icon-tooltip-circle'
                />
              </div>
              <div className='params_panel__param__property'>
                <Text size={12} className='stats_panel__stat__value'>
                  {tradesNumber}
                </Text>
                {/* <div className='params_panel__param__property__icon icon-edit' /> */}
              </div>
            </div>
            <ReactTooltip
              id='limit-stats'
              type='dark'
              place='bottom'
              effect='solid'
              className='text_tooltip'
              multiline={true}
            />
          </div>
          <div className='trading__footer'>
            {/* <div className='trading__footer__info_box'>
              <div className='trading__footer__info_box__item'>
                <Text size={12}>3.1200ETH</Text>
              </div>
              <div className='trading__footer__info_box__item'>
                <Text size={12}>$1360.00</Text>
                <div className='trading__footer__info_box__item__counter'>
                  <Text size={10}>3</Text>
                </div>
              </div>
            </div> */}
            <div className='row'>
              <ControlItem
                disable={!account}
                imageUrl={wallet}
                click={() =>
                  appDispatch(actionShowModal(ModalTypes.limitOrderProxy, {}))
                }
              />
              <ControlItem
                disable={!account}
                imageUrl={settings}
                click={() => setTab(ETabs.LSettings)}
              />
            </div>
          </div>
        </>
      );
    }
  };

  return (
    <div className='container' ref={widget}>
      {renderContent()}
    </div>
  );
};

export default Limit;
